import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import {
  ArrowLeft,
  Receipt,
  Globe,
  Loader2,
  RefreshCw,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /admin/tax-buckets — Quarterly remittance dashboard. Rows are aggregated
 * from the signed `tax_buckets` ledger, grouped by (country, state). Each
 * row shows collected − reversed − remitted = owed. Admin can mark a
 * bucket as remitted (PIN-gated). ZIP5 rooftop import is exposed as a
 * text-area paste for CSV bulk upload.
 */
const AdminTaxBuckets = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quarter, setQuarter] = useState("");
  const [chainStatus, setChainStatus] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const url = quarter ? `${API}/admin/tax/buckets/summary?quarter=${quarter}` : `${API}/admin/tax/buckets/summary`;
      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tax buckets");
    } finally {
      setLoading(false);
    }
  }, [token, quarter]);

  useEffect(() => { load(); }, [load]);

  const verifyChain = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/tax/buckets/verify`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setChainStatus(data);
      toast.success(data.valid ? `Ledger valid · ${data.checked} rows verified` : `LEDGER BROKEN at ${data.broken_at}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chain verify failed");
    }
  };

  const remit = async (row) => {
    const pin = window.prompt("MASTER_SECURITY_PIN required to mark bucket as remitted:", "");
    if (!pin) return;
    const memo = window.prompt("Filing memo (e.g. CRA GST/HST Q1 2026)?", "");
    try {
      await axios.post(
        `${API}/admin/tax/buckets/remit`,
        { quarter: summary?.quarter || "ALL", country: row.country, state: row.state, memo: memo || "" },
        { headers: { Authorization: `Bearer ${token}`, "X-Master-Security-Pin": pin } },
      );
      toast.success(`Remitted $${(row.owed_cents / 100).toFixed(2)} for ${row.country}${row.state ? `/${row.state}` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remit failed");
    }
  };

  const reseed = async () => {
    if (!window.confirm("Reseed the global rate matrix from the latest snapshot? This is idempotent — existing rows are refreshed to the newest rates.")) return;
    try {
      const { data } = await axios.post(`${API}/admin/tax/reseed`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Reseeded ${data.upserted} rate rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reseed failed");
    }
  };

  if (!user) return null;

  const rows = summary?.rows || [];
  const totalOwed = summary?.total_owed_cents || 0;

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-tax-buckets-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center" data-testid="taxbuckets-back-btn" aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">Admin · Tax Buckets</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#F0C464]" />
            <span className="text-sm text-[#8A92A0]" data-testid="taxbuckets-owed">${(totalOwed / 100).toFixed(2)} owed</span>
          </div>
        </div>
      </header>
      <AdminNavigation />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">Quarterly Tax Buckets</h1>
            <p className="text-[#8A92A0] max-w-3xl text-sm">
              Every dollar of sales tax collected lands in the appropriate country/state bucket via the signed <code className="text-[#F0C464]">tax_buckets</code> ledger. Refunds automatically reverse. Mark a bucket as <strong>remitted</strong> once you&apos;ve filed the return with that jurisdiction.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={quarter}
              onChange={(e) => setQuarter(e.target.value.toUpperCase().replace(/[^0-9Q]/g, ""))}
              placeholder="2026Q1"
              className="w-24 px-2 py-1.5 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm font-mono uppercase"
              data-testid="tax-quarter-input"
            />
            <button
              onClick={load}
              className="h-9 px-3 rounded-full border border-[#1A2030] text-[#8A92A0] text-xs font-bold flex items-center gap-1 hover:text-white"
              data-testid="tax-reload-btn"
            >
              <RefreshCw className="w-3 h-3" /> Reload
            </button>
            <button
              onClick={verifyChain}
              className="h-9 px-3 rounded-full border border-[#8DB87A]/40 text-[#8DB87A] text-xs font-bold flex items-center gap-1 hover:bg-[#8DB87A]/10"
              data-testid="tax-chain-verify-btn"
            >
              <ShieldCheck className="w-3 h-3" /> Verify chain
            </button>
            <button
              onClick={reseed}
              className="h-9 px-3 rounded-full border border-[#F0C464]/40 text-[#F0C464] text-xs font-bold hover:bg-[#F0C464]/10"
              data-testid="tax-reseed-btn"
            >
              Reseed matrix
            </button>
          </div>
        </div>

        {chainStatus && (
          <div className={`card p-3 mb-6 text-sm ${chainStatus.valid ? "border-[#8DB87A]/40" : "border-[#EC008C]/40"}`} data-testid="tax-chain-status">
            {chainStatus.valid ? `✅ Signed ledger valid — ${chainStatus.checked} rows verified.` : `❌ Chain broken at ${chainStatus.broken_at}. Do not remit until this is investigated.`}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#F0C464]" /></div>
        ) : (
          <>
            <div className="card p-5 mb-6" data-testid="tax-totals-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Quarter</p>
                  <p className="text-white font-['Unbounded'] text-2xl font-bold">{summary?.quarter || "ALL"}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Total owed to jurisdictions</p>
                  <p className="text-[#F0C464] font-['Unbounded'] text-3xl font-bold">${(totalOwed / 100).toFixed(2)}</p>
                </div>
              </div>
            </div>

            {rows.length === 0 ? (
              <div className="card p-16 text-center" data-testid="tax-buckets-empty">
                <Globe className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">No tax collected in this window</h3>
                <p className="text-[#8A92A0] text-sm">Buckets appear here the moment a paid sale routes tax into a country/state.</p>
              </div>
            ) : (
              <div className="card overflow-hidden" data-testid="tax-buckets-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#1A2030] text-left text-[10px] uppercase tracking-widest text-[#8A92A0]">
                        <th className="px-4 py-3">Country</th>
                        <th className="px-4 py-3">State/Region</th>
                        <th className="px-4 py-3 text-right">Collected</th>
                        <th className="px-4 py-3 text-right">Reversed</th>
                        <th className="px-4 py-3 text-right">Remitted</th>
                        <th className="px-4 py-3 text-right">Owed</th>
                        <th className="px-4 py-3 text-right">Txns</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={`${r.country}_${r.state}`} className="border-b border-[#1A2030]/50 hover:bg-[#0A0E18]" data-testid={`tax-row-${r.country}-${r.state || "none"}`}>
                          <td className="px-4 py-3 text-white font-bold">{r.country || "—"}</td>
                          <td className="px-4 py-3 text-[#8A92A0]">{r.state || "—"}</td>
                          <td className="px-4 py-3 text-right font-mono text-[#8DB87A]">${(r.collected_cents / 100).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-mono text-[#EC008C]">-${(r.reversed_cents / 100).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-mono text-[#8A92A0]">${(r.remitted_cents / 100).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-mono text-[#F0C464] font-bold">${(r.owed_cents / 100).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-[#8A92A0]">{r.txn_count}</td>
                          <td className="px-4 py-3 text-right">
                            {r.owed_cents > 0 && (
                              <button
                                onClick={() => remit(r)}
                                className="text-[#F0C464] text-xs font-bold hover:underline flex items-center gap-1 ml-auto"
                                data-testid={`tax-remit-btn-${r.country}-${r.state || "none"}`}
                              >
                                Mark remitted <ChevronRight className="w-3 h-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminTaxBuckets;
